import { useEffect, useCallback } from "react";
import { useProjectStore } from "@/stores/project-store";
import { wsClient } from "@/lib/ws-client";

/**
 * Shared hook for Claude WebSocket integration.
 * Handles connection, message routing, input, and resize.
 */
export function useClaude() {
  const { claudeRunning, setClaudeRunning, addConsoleLine, consoleLines, activeWorkspaceId } = useProjectStore();

  useEffect(() => {
    if (!wsClient.connected) wsClient.connect();
  }, []);

  useEffect(() => {
    const unsub = wsClient.onMessage((msg) => {
      switch (msg.type) {
        case "console_output":
          if (typeof msg.data === "string") addConsoleLine(msg.data);
          break;
        case "claude_started":
          setClaudeRunning(true);
          break;
        case "claude_stopped":
          setClaudeRunning(false);
          break;
        case "error":
          if (msg.message === "Claude is already running") setClaudeRunning(true);
          break;
      }
    });
    return unsub;
  }, [addConsoleLine, setClaudeRunning]);

  const toggleClaude = useCallback(() => {
    if (claudeRunning) {
      wsClient.send({ type: "stop_claude" });
    } else {
      wsClient.send({ type: "start_claude", projectId: activeWorkspaceId });
    }
  }, [claudeRunning, activeWorkspaceId]);

  const handleInput = useCallback(
    (data: string) => {
      if (claudeRunning) {
        wsClient.send({ type: "claude_input", data });
      }
    },
    [claudeRunning],
  );

  const handleResize = useCallback((cols: number, rows: number) => {
    wsClient.send({ type: "claude_resize", cols, rows });
  }, []);

  return { claudeRunning, consoleLines, toggleClaude, handleInput, handleResize };
}
